import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import {
  LegacyApi,
  ModernApi,
  ProjectSettingApi,
} from "../../api/project/types";
import {
  getProjectContracts,
  GetProjectContractsRequest,
} from "./get-project-contracts";
import {
  getProjectParticipants,
  getProject,
  getProjectAdmin,
  getProjectSettings,
  getProjects,
  getProjectsLegacy,
  getProjectUser,
} from ".";
import type {
  GetProjectParticipantsRequest,
  GetProjectRequest,
  GetProjectAdminRequest,
  GetProjectUserRequest,
} from ".";

export const projectQueries = {
  rootKey: () => ["project"],

  getProject: ({ projectId, idType }: GetProjectRequest) =>
    queryOptions({
      queryKey: [...projectQueries.rootKey(), projectId, "getProject"],
      queryFn: () => getProject({ projectId, idType }),
    }),

  getProjects: ({
    page = 1,
    limit = 1000,
  }: Partial<ModernApi.GetProjectsRequest>) =>
    queryOptions({
      queryKey: [...projectQueries.rootKey(), "getProjects", { page, limit }],
      queryFn: () => getProjects({ page, limit }),
    }),

  getProjectsLegacy: ({
    pageNumber,
    pageSize,
  }: Partial<LegacyApi.GetProjectsRequest>) =>
    queryOptions({
      queryKey: [
        ...projectQueries.rootKey(),
        "getProjectsLegacy",
        { pageNumber, pageSize },
      ],
      queryFn: () => getProjectsLegacy({ pageNumber, pageSize }),
    }),

  getProjectAdmin: ({ projectId }: GetProjectAdminRequest) =>
    queryOptions({
      queryKey: [...projectQueries.rootKey(), projectId, "getProjectAdmin"],
      queryFn: () => getProjectAdmin({ projectId }),
    }),

  getProjectContracts: ({ projectId }: GetProjectContractsRequest) =>
    queryOptions({
      queryKey: [...projectQueries.rootKey(), projectId, "getProjectContracts"],
      queryFn: () => getProjectContracts({ projectId }),
    }),

  getProjectParticipants: ({
    projectId,
    ...rest
  }: GetProjectParticipantsRequest) =>
    queryOptions({
      queryKey: [
        ...projectQueries.rootKey(),
        projectId,
        "getProjectParticipants",
        rest,
      ],
      queryFn: () => getProjectParticipants({ projectId, ...rest }),
    }),

  getProjectParticipantsInfinite: ({
    projectId,
    ...rest
  }: Omit<GetProjectParticipantsRequest, "page">) =>
    infiniteQueryOptions({
      queryKey: [
        ...projectQueries.rootKey(),
        projectId,
        "getProjectParticipants",
        rest,
      ],
      queryFn: ({ pageParam }) =>
        getProjectParticipants({ projectId, page: pageParam, ...rest }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage.total < lastPage.page * lastPage.limit) {
          return undefined;
        }
        return lastPage.page + 1;
      },
    }),

  getOrganizationUserInfinite: ({
    projectId,
    projectParticipantId,
    ...rest
  }: Omit<GetProjectUserRequest, "page">) =>
    infiniteQueryOptions({
      queryKey: [
        ...projectQueries.rootKey(),
        projectId,
        projectParticipantId,
        "getOrganizationUser",
        rest,
      ],
      
      queryFn: ({ pageParam }) =>
        getProjectUser({
          projectId,
          projectParticipantId,
          page: pageParam,
          ...rest,
          userEmail: rest.userEmail ?? 'JUST_IGNORE_ME_0_VEHICLE_INVENTED',
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage.total < lastPage.page * lastPage.limit) {
          return undefined;
        }
        return lastPage.page + 1;
      },
    }),

  getProjectSettings: ({
    projectId,
  }: ProjectSettingApi.ProjectSettingsRequest) =>
    queryOptions({
      queryKey: [...projectQueries.rootKey(), "getProjectSettings", projectId],
      queryFn: () => getProjectSettings({ projectId }),
    }),
};
